import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { injectApiUrl } from '@seatix/shared-config';
import {
  ApiResponse,
  AssignedEventWithGates,
  ForgotPasswordResponse,
  LoginCredentials,
  LoginResponse,
  RegisterCredentials,
  RegisterResponse,
  ResetPasswordResponse,
  SocialAuthResponse,
  TokenLoginResponse,
  User,
  ValidateTokenResponse,
} from '@seatix/shared/domain';
import { Observable } from 'rxjs';

@Injectable()
export class AuthApiService {
  readonly apiUrl = injectApiUrl();
  readonly http = inject(HttpClient);

  login(credentials: LoginCredentials, turnstileToken: string) {
    return this.http.post<LoginResponse>(
      `${this.apiUrl}/api/auth/login/session`,
      {
        ...credentials,
        turnstile_token: turnstileToken,
      },
    );
  }

  loginWithToken(credentials: LoginCredentials, turnstileToken?: string) {
    return this.http.post<TokenLoginResponse>(
      `${this.apiUrl}/api/auth/login/token`,
      {
        ...credentials,
        turnstile_token: turnstileToken,
      },
    );
  }

  getUserData(): Observable<ApiResponse<User>> {
    return this.http.get<ApiResponse<User>>(`${this.apiUrl}/api/auth/me`);
  }

  getAssignedGates(): Observable<ApiResponse<AssignedEventWithGates[]>> {
    return this.http.get<ApiResponse<AssignedEventWithGates[]>>(
      `${this.apiUrl}/api/auth/me/gates`,
    );
  }

  register(credentials: RegisterCredentials, turnstileToken: string) {
    return this.http.post<RegisterResponse>(
      `${this.apiUrl}/api/auth/signup`,
      {
        ...credentials,
        turnstile_token: turnstileToken,
      },
      {
        params: { mode: 'session' },
      },
    );
  }

  logout(): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.apiUrl}/api/auth/me/logout/session`,
      {},
    );
  }

  handleGoogleCallback(token: string) {
    return this.http.post<SocialAuthResponse>(
      `${this.apiUrl}/api/auth/social/google/callback`,
      { token },
    );
  }

  verifyEmail(code: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/api/auth/verify`,
      { code },
    );
  }

  resendVerification() {
    return this.http.post<ApiResponse<null>>(
      `${this.apiUrl}/api/auth/send-verification`,
      {},
    );
  }

  patchUser(user: Partial<User>) {
    return this.http.patch<ApiResponse<Partial<User>>>(
      `${this.apiUrl}/api/auth/me`,
      user,
    );
  }

  requestPasswordReset(email: string, turnstile_token: string) {
    return this.http.post<ForgotPasswordResponse>(
      `${this.apiUrl}/api/auth/password/forgot`,
      { email, turnstile_token },
    );
  }

  validateResetToken(token: string, email: string) {
    return this.http.get<ValidateTokenResponse>(
      `${this.apiUrl}/api/auth/password/reset/validate`,
      { params: { token, email } },
    );
  }

  resetPassword(
    credentials: {
      email: string;
      token: string;
      password: string;
      password_confirmation: string;
    },
    turnstileToken: string,
  ) {
    return this.http.post<ResetPasswordResponse>(
      `${this.apiUrl}/api/auth/password/reset`,
      {
        email: credentials.email,
        token: credentials.token,
        password: credentials.password,
        password_confirmation: credentials.password_confirmation,
        turnstile_token: turnstileToken,
      },
    );
  }
}
